import React from 'react';
import {
	Box,
	Heading,
	Text,
	VStack,
	HStack,
	Button,
	Badge,
	Avatar,
	AvatarGroup,
	Icon,
	Spinner,
	SimpleGrid,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import {
	FiUsers,
	FiSettings,
	FiAlertTriangle,
	FiFileText,
} from 'react-icons/fi';
import Card from 'components/card/Card';
import { usePermissions } from 'hooks/usePermissions';
import { userLabel } from 'components/userPicker/UserPicker';
import { useListSettingsChannelsQuery, useListAssignmentsQuery } from 'api/metaWhatsappApi';
import ManageDrawer from './ManageDrawer';

/**
 * Who works which number — reachable at any time, independent of setup.
 *
 * ★ WHY THIS IS ITS OWN TAB RATHER THAN A SECTION OF SETUP.
 * Assignment lived inside a number card on the setup screen, which meant the
 * only route to it ran through a connect panel. Setup happens once; deciding
 * who answers a number happens whenever someone joins, leaves or changes team.
 * An operational act reached only through a one-time wizard reads as though it
 * were part of the wizard — which is exactly why people asked whether changing
 * an assignment meant reconnecting Meta. It does not, and now the screen says
 * so by existing separately.
 *
 * ★ CONNECTION STATE IS SHOWN, NEVER CHANGED. A number that Meta has
 * disconnected cannot be fixed from here, and pretending otherwise would put a
 * connect button in front of someone doing a two-second reassignment. The
 * warning links back to Setup instead.
 */

/** Where the numbers are actually connected. */
const SETUP_TAB = '/whatsapp/settings?tab=meta';

/** The people on one number, summarised. Its own query, one per row. */
function AssignedSummary({ phoneNumberId }) {
	const { data: assignments = [], isLoading } = useListAssignmentsQuery(phoneNumberId);

	if (isLoading) return <Spinner size="xs" />;

	if (assignments.length === 0) {
		return (
			<Text fontSize="xs" color="warning">
				Nobody assigned
			</Text>
		);
	}

	// ★ ASSIGNED IS NOT THE SAME AS ABLE TO SEE IT. Everyone on the number
	// lacking the WhatsApp module is the same outcome as nobody being on it.
	const blocked = assignments.filter((a) => a.user?.canUseInbox === false).length;
	const holding = assignments.reduce((n, a) => n + (a.heldConversations ?? 0), 0);

	return (
		<HStack spacing="2.5">
			<AvatarGroup size="xs" max={4}>
				{assignments.map((a) => (
					<Avatar key={a._id} name={userLabel(a.user)} src={a.user?.profileImage} />
				))}
			</AvatarGroup>
			<Box minW="0">
				<Text fontSize="xs" color={blocked === assignments.length ? 'warning' : 'text.secondary'}>
					{assignments.length} assigned
					{blocked > 0 && ` · ${blocked} without inbox access`}
				</Text>
				{holding > 0 && (
					<Text fontSize="0.65rem" color="text.muted">
						{holding} open conversation{holding === 1 ? '' : 's'} held
					</Text>
				)}
			</Box>
		</HStack>
	);
}

function NumberRow({ channel, onManage, canAssign, canTemplatesView }) {
	const blockers = channel.blockers ?? [];
	
	const templateRoute = (channelId) =>
	`/whatsapp/settings/meta/templates/${encodeURIComponent(
		channelId
	)}`;

	console.log({canTemplatesView, channelId: channel.channelId,});

	return (
		<Card bg="bg.surface" borderWidth="1px" borderColor="border.default">
			<VStack align="stretch" spacing="3">
				<HStack justify="space-between" align="start" spacing="3" flexWrap="wrap">
					<Box minW="0">
						<Heading size="sm" color="text.primary">
							{channel.label || channel.displayPhoneNumber || channel.phoneNumberId}
						</Heading>
						{channel.label && (
							<Text fontSize="xs" color="text.muted" mt="0.5">
								{channel.displayPhoneNumber}
							</Text>
						)}
					</Box>
					<HStack spacing="2">
						{channel.isEnabled === false && (
							<Badge colorScheme="gray" variant="subtle">
								Sending off
							</Badge>
						)}
						{blockers.length > 0 && (
							<Badge colorScheme="red" variant="subtle">
								Needs attention
							</Badge>
						)}
					</HStack>
				</HStack>

				<AssignedSummary phoneNumberId={channel.phoneNumberId} />

				{/* Shown, not fixable here — the link goes where it can be fixed. */}
				{blockers.length > 0 && (
					<HStack spacing="2" align="start">
						<Icon as={FiAlertTriangle} color="warning" boxSize="3.5" mt="0.5" />
						<Text fontSize="xs" color="text.muted">
							This number has a connection problem. Assignments still work;{' '}
							<Box as={RouterLink} to={SETUP_TAB} color="text.accent" textDecoration="underline">
								open Setup
							</Box>{' '}
							to fix it.
						</Text>
					</HStack>
				)}


				<HStack
	spacing="2"
	flexWrap="wrap"
>
	<Button
		size="sm"
		variant="outline"
		leftIcon={
			<Icon as={FiUsers} />
		}
		onClick={() => onManage(channel)}
		isDisabled={!canAssign}
	>
		Manage people
	</Button>

	{channel.channelId && canTemplatesView && (
		<Button
			as={RouterLink}
			to={templateRoute(
				channel.channelId
			)}
			size="sm"
			variant="ghost"
			leftIcon={
				<Icon as={FiFileText} />
			}
		>
			Templates
		</Button>
	)}
</HStack>
			</VStack>
		</Card>
	);
}

export default function MetaWhatsappAssignments() {
	const { hasPermission } = usePermissions();
	const canAssign = hasPermission('whatsapp', 'channel.assign');
	const canTemplatesView = hasPermission('whatsapp', 'template.view');

	const { data, isLoading, isError } = useListSettingsChannelsQuery();
	// A "needs configuration" row is a fault to repair in Setup, not a number
	// anyone can be assigned to — it has no local record to hang a grant on.
	const channels = (data?.channels ?? []).filter((c) => !c.needsConfiguration);

	const [managing, setManaging] = React.useState(null);

	if (isLoading) return <Spinner size="sm" />;
	if (isError) {
		return (
			<Text fontSize="sm" color="danger">
				Assignments could not be loaded.
			</Text>
		);
	}


	if (channels.length === 0) {
		return (
			<Card bg="bg.surface" borderWidth="1px" borderColor="border.default">
				<VStack align="start" spacing="3">
					<Text fontSize="sm" fontWeight="medium" color="text.primary">
						No WhatsApp numbers yet
					</Text>
					<Text fontSize="xs" color="text.muted">
						Connect a number first — there is nobody to assign until there is something to assign
						them to.
					</Text>
					<Button as={RouterLink} to={SETUP_TAB} size="sm" leftIcon={<Icon as={FiSettings} />}>
						Open Setup
					</Button>
				</VStack>
			</Card>
		);
	}

	return (
		<VStack align="stretch" spacing="4" maxW="1400px" mx="auto">
			<Box>
				<Text fontSize="sm" color="text.muted">
					Only assigned people see a number&apos;s conversations or can send from it. Changing this
					never touches the Meta connection.
				</Text>
			</Box>

			<SimpleGrid columns={{ base: 1, lg: 2 }} spacing="4" alignItems="start">
				{channels.map((c) => (
					<NumberRow
						key={c.phoneNumberId}
						channel={c}
						canAssign={canAssign}
						onManage={setManaging}
						canTemplatesView={canTemplatesView}
					/>
				))}
			</SimpleGrid>

			<ManageDrawer
				channel={managing}
				isOpen={Boolean(managing)}
				onClose={() => setManaging(null)}
			/>
		</VStack>
	);
}
